import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { gridModels } from "@/mocks/models";

export default function ModelGrid() {
  const [favorites, setFavorites] = useState<Set<number>>(new Set());

  const toggleFavorite = (id: number) => {
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const getBadgeStyle = (badge: string) => {
    switch (badge) {
      case "ライブ":
        return "bg-primary-500/90 text-white";
      case "VIP":
        return "bg-accent-500/90 text-white";
      case "NEW":
        return "bg-green-500/90 text-white";
      case "HOT":
        return "bg-orange-500/90 text-white";
      case "TOP":
        return "bg-yellow-500/90 text-background-50";
      case "近所":
        return "bg-blue-500/90 text-white";
      case "18+":
        return "bg-red-500/90 text-white";
      default:
        return "bg-primary-500/90 text-white";
    }
  };

  return (
    <section className="px-4 py-4">
      <div className="mb-4 flex items-end justify-between">
        <h2 className="text-lg font-bold text-foreground-50 md:text-xl">
          近くでライブ中の女の子たち
          <span className="text-primary-400"> 💗</span>
        </h2>
        <a
          href="#download"
          className="shrink-0 text-sm font-medium text-primary-400 transition-colors hover:text-primary-300"
        >
          すべて見る <i className="ri-arrow-right-s-line" />
        </a>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
        {gridModels.map((model, index) => (
          <motion.a
            key={model.id}
            href="#download"
            className="group relative overflow-hidden rounded-xl bg-background-100"
            initial={{ opacity: 0, y: 36 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{
              duration: 0.4,
              delay: index * 0.06,
              ease: [0.25, 0.4, 0.25, 1],
            }}
            whileHover={{ scale: 1.03, y: -2 }}
            whileTap={{ scale: 0.96 }}
          >
            <div className="relative aspect-[3/4] w-full overflow-hidden">
              <img
                src={model.image}
                alt={`${model.name} ${model.age}歳`}
                className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-110"
              />
              {/* Overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />

              {/* Badge */}
              <span
                className={`absolute left-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-bold backdrop-blur-sm ${getBadgeStyle(model.badge)}`}
              >
                {model.badge === "ライブ" && (
                  <>
                    <i className="ri-fire-fill mr-0.5" />
                    ライブ
                  </>
                )}
                {model.badge === "18+" && "🔞 18+"}
                {model.badge === "NEW" && (
                  <>
                    <i className="ri-sparkling-line mr-0.5" />
                    新人
                  </>
                )}
                {model.badge === "HOT" && (
                  <>
                    <i className="ri-fire-fill mr-0.5" />
                    激熱
                  </>
                )}
                {model.badge === "TOP" && (
                  <>
                    <i className="ri-vip-crown-fill mr-0.5" />
                    TOPアイドル
                  </>
                )}
                {model.badge === "近所" && (
                  <>
                    <i className="ri-map-pin-fill mr-0.5" />
                    近くの子
                  </>
                )}
              </span>

              {/* Heart button */}
              <motion.button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  toggleFavorite(model.id);
                }}
                className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm transition-colors hover:bg-white/30"
                whileTap={{ scale: 0.8 }}
              >
                <AnimatePresence mode="wait">
                  <motion.i
                    key={favorites.has(model.id) ? "filled" : "line"}
                    className={
                      favorites.has(model.id)
                        ? "ri-heart-3-fill text-primary-400"
                        : "ri-heart-3-line"
                    }
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.5, opacity: 0 }}
                    transition={{ type: "spring", stiffness: 500, damping: 25 }}
                  />
                </AnimatePresence>
              </motion.button>

              {/* Online dot */}
              {model.isLive && (
                <span className="absolute bottom-2 right-2 h-2.5 w-2.5 rounded-full border border-white/50 bg-green-400" />
              )}

              {/* Info */}
              <div className="absolute bottom-0 left-0 right-0 p-2.5">
                <div className="text-sm font-bold text-white">
                  {model.name}、{model.age}歳
                </div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {model.tags.map((tag, i) => (
                    <span
                      key={i}
                      className="rounded bg-white/20 px-1.5 py-0.5 text-[10px] font-medium text-white/90 backdrop-blur-sm"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </motion.a>
        ))}
      </div>
    </section>
  );
}